import type { PropsWithChildren } from 'react';
import type React from 'react';
import { useLayoutEffect, useRef } from 'react';

import { cn } from '@change/ds-utils';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { SocialProof } from '#src/app/pages/petitionDetails/shared/components';
import { usePageScrollPosition, useSetStickyActionBarHeight } from '#src/app/pages/petitionDetails/shared/hooks';

type Props = {
	sticky: boolean;
	visible: boolean;
	hideSocialProof?: boolean;
};

export const ActionButtonWrapper: React.FC<PropsWithChildren<Props>> = ({
	children,
	sticky,
	visible,
	hideSocialProof = false,
}) => {
	const setStickyActionBarHeight = useSetStickyActionBarHeight();
	const { afterMobileActionButton } = usePageScrollPosition();
	const { errorReporter } = useUtilityContext();
	const wrapperRef = useRef<HTMLDivElement>(null);

	// `visible` is false both when the bar is parked and when `AfterMobileActionButtonWaypoint` has
	// not reported yet (see `calculateVisibility` in MobileActionButton.tsx). Only the first means
	// the bar leaves the viewport uncovered, so the second counts as covering until it resolves.
	const coversViewport = visible || afterMobileActionButton === undefined;

	// Sole writer of the sticky action bar height — see `StickyActionBarHeightContext` for what the
	// published value means and CHANGE-99504 for the occlusion bug it fixes.
	//
	// Two instances of this component mount per page (an in-flow copy and a sticky fixed copy)
	// sharing this one setter, so the non-sticky instance bails out below rather than clobbering
	// what the sticky one published.
	//
	// A layout effect rather than a plain one, so the height is published before the first paint.
	// A consumer's IntersectionObserver can deliver its first notification in the same task as the
	// waypoint that resolves `visible`, so a height that arrived any later would let it fire against
	// the un-offset viewport on a load that starts with a tracked section already on screen.
	useLayoutEffect(() => {
		if (!sticky) {
			return undefined;
		}

		// Parked off-screen (`bottom-[-1000px]`), so the bar hides nothing. Publishing its height here
		// would hold back a section that is in plain view, e.g. the decision-maker section on a
		// petition with a short description, which is on screen before the bar docks.
		if (!coversViewport) {
			setStickyActionBarHeight(0);
			return undefined;
		}

		const element = wrapperRef.current;

		// Structurally unreachable today: the ref'd `<div>` below renders unconditionally whenever
		// `sticky` is true. Reported rather than silently publishing 0, so a regression here stays
		// distinguishable from the expected "no bar on this page" case.
		if (!element) {
			void errorReporter.report(
				{ error: new Error('ActionButtonWrapper: sticky action bar ref not attached while sticky') },
				{ severity: 'warning' },
			);
			setStickyActionBarHeight(0);
			return undefined;
		}

		/**
		 * Rounded because this lands verbatim in a consumer's `rootMargin`, a dep of the underlying
		 * `useInView` callback ref — a fractional value rebuilds their IntersectionObserver on every
		 * sub-pixel reflow, where a rounded one is a setState React bails out of.
		 */
		const publishHeight = () => {
			setStickyActionBarHeight(Math.round(element.getBoundingClientRect().height));
		};

		// Synchronously, not via the observer's first callback: ResizeObserver reports nothing for a
		// target that is already 0x0 — exactly this instance at `md` and up, where its ancestor is
		// `display: none` — so a consumer awaiting that callback would never track at all.
		publishHeight();

		// Warned because the height then stays frozen at the measurement above, never following a
		// reflow. Still resets on unmount, so a stale height can't outlive the bar.
		if (typeof ResizeObserver === 'undefined') {
			void errorReporter.report(
				{
					error: new Error(
						'ActionButtonWrapper: ResizeObserver unsupported, cannot track sticky action bar height changes',
					),
				},
				{ severity: 'warning' },
			);
			return () => {
				setStickyActionBarHeight(0);
			};
		}

		// Re-reads the element rather than the callback's `contentRect`, so both paths publish the
		// same border-box height.
		const observer = new ResizeObserver(publishHeight);
		observer.observe(element);

		return () => {
			observer.disconnect();
			setStickyActionBarHeight(0);
		};
	}, [sticky, coversViewport, setStickyActionBarHeight, errorReporter]);

	if (!sticky) {
		return visible ? <div>{children}</div> : null;
	}

	const wrapperVisibilityClasses = visible ? 'bottom-0 focus-within:bottom-0' : 'bottom-[-1000px]';

	return (
		// this approach ensures the button follows the `visible` flag even after it has been focused, while
		// still preventing outlines from appearing around the wrapper when focus is managed programmatically.
		<div
			ref={wrapperRef}
			className={cn(
				'fixed left-0 z-29 w-full shadow-[0px_-0.7px_1.4px_rgba(0,0,0,0.07),0px_-1.9px_4px_rgba(0,0,0,0.05),0px_-4.5px_10px_rgba(0,0,0,0.05),0px_-15px_32px_rgba(0,0,0,0.04)] focus-within:outline-none',
				wrapperVisibilityClasses,
			)}
		>
			<div className="bg-background-raised flex flex-col gap-2 px-4 pt-2 pb-2">
				{!hideSocialProof && <SocialProof />}
				{children}
			</div>
		</div>
	);
};
